/**
 * Flashka web sound system — uisfx (`zen` pack) behind the shared event map.
 *
 * `initWebSounds()` is called once from `main.tsx` (client only). After
 * that, any code may fire `playSound('<event>')` — muted/cooled-down/locked
 * calls are silent no-ops. See `shared/src/sound/cues.ts` for the event → cue
 * map.
 */

import {
  configureSound,
  playSound,
  playSoundAfterCurrent,
  resultSoundForPercent,
  unlockAudio,
} from '@flashka/shared/sound';

import { installGestureUnlock, webSoundAdapter } from './player';
import { getSoundsEnabled } from './sound-prefs';
import { installTypingSound } from './typing';

export { playSound, playSoundAfterCurrent, resultSoundForPercent, unlockAudio };
export {
  getPack,
  getSoundsEnabled,
  getVolume,
  setSoundPack,
  setSoundsEnabled,
  setSoundVolume,
  useSoundPack,
  useSoundsEnabled,
  useSoundVolume,
} from './sound-prefs';
export type { SoundEvent } from '@flashka/shared/sound';
// Registry + live-retune surface (the Sound Cues story drives these so it
// tunes the REAL mapping rather than a copy).
export {
  clearCueOverrides,
  DEFAULT_SOUND_PACK,
  EVENT_CUES,
  getCueOverrides,
  LOCAL_SOUND_ASSETS,
  type LocalSoundAsset,
  packLabel,
  packsDefaultFirst,
  parseSourceKey,
  setCueOverride,
  type SoundSource,
  sourceKey,
} from '@flashka/shared/sound';

export function initWebSounds(): void {
  configureSound({ adapter: webSoundAdapter, isEnabled: getSoundsEnabled });
  installGestureUnlock();
  // One delegated `input` listener covers every text field in the app.
  installTypingSound();
}
